import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { subirRoteiro, removerRoteiro } from '../lib/portal';

const PLATAFORMAS = {
  'Instagram': { cor: '#D4789B', icone: '📷' },
  'TikTok':    { cor: '#1a1a1a', icone: '🎵' },
  'YouTube':   { cor: '#E05454', icone: '▶️' },
  'LinkedIn':  { cor: '#4A90D9', icone: '💼' },
  'Facebook':  { cor: '#3b5998', icone: '👍' },
  'Outro':     { cor: '#9B7FD4', icone: '✨' },
};
const FORMATOS = ['Criativo', 'Reels', 'Vídeo TikTok', 'Carrossel', 'Post', 'Stories', 'Vídeo longo', 'Post estático', 'Live'];
const STATUS_PRODUCAO = ['Planejado', 'Roteiro pronto', 'Gravado', 'Editado', 'Agendado', 'Publicado'];
const PERFORMANCE_OPCOES = ['Bom', 'Médio', 'Viralizou', 'Trouxe lead'];
const HORIZONTE_ACERVO_DIAS = 14;

function hoje() { return new Date().toISOString().split('T')[0]; }
const CONTEUDO_VAZIO = { projeto_id:'', data: hoje(), plataforma:'Instagram', formato:'Reels', titulo:'', roteiro:'', responsavel:'', status:'Planejado', concluido:false, performance:'', obs_performance:'' };

// Puxa do acervo (conteúdos de TikTok sem data) pra preencher os
// próximos ~2 semanas, respeitando o ritmo/dia configurado por
// projeto. Retorna true se mexeu em algo (pra saber se precisa recarregar).
async function autoPreencherAcervoTiktok(conteudos, projetos) {
  const amanha = new Date(); amanha.setDate(amanha.getDate() + 1);
  const limite = new Date(amanha); limite.setDate(limite.getDate() + HORIZONTE_ACERVO_DIAS);
  const amanhaStr = amanha.toISOString().split('T')[0];

  const porProjeto = {};
  conteudos.forEach(c => { if (c.projeto_id) (porProjeto[c.projeto_id] ||= []).push(c); });

  const atualizacoes = [];
  for (const projetoId of Object.keys(porProjeto)) {
    const ritmo = projetos.find(p => p.id === projetoId)?.ritmo_tiktok_dia || 1;
    const tiktokDoProjeto = porProjeto[projetoId].filter(c => c.formato === 'Vídeo TikTok' && c.plataforma === 'TikTok');
    const acervo = tiktokDoProjeto.filter(c => !c.data).sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    if (acervo.length === 0) continue;

    const ocupacao = {};
    tiktokDoProjeto.filter(c => c.data && c.data >= amanhaStr).forEach(c => { ocupacao[c.data] = (ocupacao[c.data] || 0) + 1; });

    const cursor = new Date(amanha);
    let idx = 0;
    while (cursor <= limite && idx < acervo.length) {
      const dataStr = cursor.toISOString().split('T')[0];
      const usado = ocupacao[dataStr] || 0;
      if (usado < ritmo) {
        atualizacoes.push({ id: acervo[idx].id, data: dataStr });
        ocupacao[dataStr] = usado + 1;
        idx++;
      } else {
        cursor.setDate(cursor.getDate() + 1);
      }
    }
  }

  for (const upd of atualizacoes) {
    await supabase.from('conteudos').update({ data: upd.data }).eq('id', upd.id);
  }
  return atualizacoes.length > 0;
}

export default function Conteudo() {
  const [conteudos, setConteudos] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [visao, setVisao] = useState('mes'); // mes | semana | dia
  const [dataRef, setDataRef] = useState(new Date());
  const [filtroProjeto, setFiltroProjeto] = useState('');

  const [itemAberto, setItemAberto] = useState(null);
  const [modalItem, setModalItem] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [form, setForm] = useState(CONTEUDO_VAZIO);
  const [modalExport, setModalExport] = useState(false);
  const [enviandoRoteiro, setEnviandoRoteiro] = useState(false);
  const [exportInicio, setExportInicio] = useState(hoje());
  const [exportFim, setExportFim] = useState(hoje());
  const [modalAcervo, setModalAcervo] = useState(false);

  useEffect(() => { carregarTudo(); }, []);

  async function carregarTudo() {
    setCarregando(true);
    const [{ data: c }, { data: p }, { data: col }] = await Promise.all([
      supabase.from('conteudos').select('*').order('data'),
      supabase.from('projetos').select('id, nome, cor, ritmo_tiktok_dia').order('nome'),
      supabase.from('colaboradores').select('nome').eq('status','Ativo').order('nome'),
    ]);
    let conteudosFinal = c || [];
    if ((p || []).length) {
      const mexeu = await autoPreencherAcervoTiktok(conteudosFinal, p);
      if (mexeu) {
        const { data: c2 } = await supabase.from('conteudos').select('*').order('data');
        conteudosFinal = c2 || [];
      }
    }
    setConteudos(conteudosFinal);
    setProjetos(p || []);
    setColaboradores(col || []);
    setCarregando(false);
  }

  const conteudosFiltrados = conteudos.filter(c => !filtroProjeto || c.projeto_id === filtroProjeto);
  const acervo = conteudosFiltrados.filter(c => !c.data);
  function nomeProjeto(id) { return projetos.find(p=>p.id===id)?.nome || '—'; }

  function conteudosDoDia(dataStr) { return conteudosFiltrados.filter(c => c.data === dataStr); }

  function abrirNovo(dataStr) {
    setItemEditando(null);
    setForm({ ...CONTEUDO_VAZIO, data: dataStr || hoje() });
    setModalItem(true);
  }
  function abrirEditar(item) {
    setItemEditando(item);
    setForm({ ...item });
    setModalItem(true);
  }

  async function salvar() {
    if (!form.titulo || !form.data) return;
    const payload = { ...form, projeto_id: form.projeto_id || null };
    if (itemEditando) {
      const { data, error } = await supabase.from('conteudos').update(payload).eq('id', itemEditando.id).select().single();
      if (error) { alert('Erro ao salvar: ' + error.message); console.error(error); return; }
      if (data) { setConteudos(conteudos.map(c => c.id===data.id?data:c)); if (itemAberto?.id===data.id) setItemAberto(data); }
    } else {
      const { data, error } = await supabase.from('conteudos').insert([payload]).select().single();
      if (error) { alert('Erro ao criar conteúdo: ' + error.message); console.error(error); return; }
      if (data) setConteudos([...conteudos, data]);
    }
    setModalItem(false); setItemEditando(null);
  }

  async function atualizarCampo(item, campo, valor) {
    const { data, error } = await supabase.from('conteudos').update({ [campo]: valor }).eq('id', item.id).select().single();
    if (!error && data) { setConteudos(conteudos.map(c => c.id===data.id?data:c)); setItemAberto(data); }
  }

  async function agendarDoAcervo(item, dataStr) {
    const { data, error } = await supabase.from('conteudos').update({ data: dataStr }).eq('id', item.id).select().single();
    if (!error && data) setConteudos(conteudos.map(c => c.id === data.id ? data : c));
  }

  async function enviarRoteiroDocx(item, file) {
    if (!item.projeto_id) { alert('Selecione um projeto neste conteúdo antes de subir o roteiro.'); return; }
    setEnviandoRoteiro(true);
    try {
      const path = await subirRoteiro(item.projeto_id, item.id, file);
      await atualizarCampo(item, 'roteiro_arquivo', path);
    } catch (e) {
      alert('Erro ao subir o roteiro: ' + e.message);
    }
    setEnviandoRoteiro(false);
  }

  async function excluirRoteiroDocx(item) {
    if (!item.roteiro_arquivo) return;
    try {
      await removerRoteiro(item.roteiro_arquivo);
      await atualizarCampo(item, 'roteiro_arquivo', null);
    } catch (e) {
      alert('Erro ao remover o roteiro: ' + e.message);
    }
  }

  async function excluir(item) {
    const { error } = await supabase.from('conteudos').delete().eq('id', item.id);
    if (!error) { setConteudos(conteudos.filter(c => c.id !== item.id)); setItemAberto(null); }
  }

  function exportarPDF() {
    const itens = conteudosFiltrados.filter(c => c.data >= exportInicio && c.data <= exportFim).sort((a,b)=>a.data.localeCompare(b.data));
    const janela = window.open('', '_blank');
    const linhas = itens.map(c => `
      <tr>
        <td>${new Date(c.data+'T00:00').toLocaleDateString('pt-BR')}</td>
        <td>${PLATAFORMAS[c.plataforma]?.icone||''} ${c.plataforma}</td>
        <td>${c.formato}</td>
        <td>${c.titulo}</td>
      </tr>`).join('');
    janela.document.write(`
      <html><head><title>Calendário de Conteúdo</title>
      <style>
        body{font-family:Arial,sans-serif;padding:30px;color:#222}
        h1{color:#6B2FA0}
        table{width:100%;border-collapse:collapse;margin-top:20px}
        th,td{border:1px solid #ddd;padding:10px;text-align:left;font-size:13px}
        th{background:#6B2FA0;color:white}
      </style></head><body>
      <h1>Calendário de Conteúdo</h1>
      <p>Período: ${new Date(exportInicio+'T00:00').toLocaleDateString('pt-BR')} a ${new Date(exportFim+'T00:00').toLocaleDateString('pt-BR')}</p>
      <table><thead><tr><th>Data</th><th>Plataforma</th><th>Formato</th><th>Tema</th></tr></thead><tbody>${linhas}</tbody></table>
      </body></html>`);
    janela.document.close();
    setTimeout(() => janela.print(), 300);
    setModalExport(false);
  }

  // ---- navegação de datas ----
  function navegar(delta) {
    const d = new Date(dataRef);
    if (visao === 'mes') d.setMonth(d.getMonth() + delta);
    else if (visao === 'semana') d.setDate(d.getDate() + delta*7);
    else d.setDate(d.getDate() + delta);
    setDataRef(d);
  }

  const nomesMes = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  function renderCard(item) {
    const plat = PLATAFORMAS[item.plataforma] || PLATAFORMAS.Outro;
    return (
      <div key={item.id} onClick={() => setItemAberto(item)} className="card"
        style={{ padding:'6px 8px', marginBottom:4, cursor:'pointer', borderLeft:`3px solid ${plat.cor}`, fontSize:11 }}>
        <div style={{ fontWeight:700, color:'var(--text-primary)' }}>{plat.icone} {item.formato}</div>
        <div style={{ color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.titulo}</div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:2 }}>
          <span style={{ fontSize:9, color:'var(--gray-400)' }}>{item.status}</span>
          <span style={{ fontSize:9 }}>{item.requer_gravacao_cliente ? '🎥' : '🏢'}</span>
          {item.concluido ? <span style={{ color:'#16a34a' }}>✓</span> : (item.data < hoje() && <span style={{ color:'#dc2626' }}>⚠</span>)}
        </div>
      </div>
    );
  }

  // ---- visão mês ----
  function renderMes() {
    const ano = dataRef.getFullYear(), mes = dataRef.getMonth();
    const primeiroDia = new Date(ano, mes, 1);
    const ultimoDia = new Date(ano, mes+1, 0);
    const offset = primeiroDia.getDay();
    const celulas = [...Array(offset).fill(null), ...Array.from({length:ultimoDia.getDate()}, (_,i)=>i+1)];
    return (
      <div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6, marginBottom:6 }}>
          {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:'var(--gray-400)' }}>{d}</div>)}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:6 }}>
          {celulas.map((dia, i) => {
            const dataStr = dia ? `${ano}-${String(mes+1).padStart(2,'0')}-${String(dia).padStart(2,'0')}` : null;
            return (
              <div key={i} onClick={() => dia && abrirNovo(dataStr)}
                style={{ minHeight:100, background: dia?'var(--bg-secondary)':'transparent', borderRadius:8, padding:6, cursor: dia?'pointer':'default' }}>
                {dia && <>
                  <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:4, fontWeight: dataStr===hoje()?700:400, color: dataStr===hoje()?'var(--purple)':'var(--gray-400)' }}>{dia}</div>
                  {conteudosDoDia(dataStr).map(item => renderCard(item))}
                </>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- visão semana ----
  function renderSemana() {
    const inicio = new Date(dataRef);
    inicio.setDate(inicio.getDate() - inicio.getDay());
    const dias = Array.from({length:7}, (_,i) => { const d = new Date(inicio); d.setDate(d.getDate()+i); return d; });
    return (
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:8 }}>
        {dias.map(d => {
          const dataStr = d.toISOString().split('T')[0];
          return (
            <div key={dataStr} style={{ background:'var(--bg-secondary)', borderRadius:8, padding:10, minHeight:220 }}>
              <div style={{ fontSize:11, fontWeight:700, color: dataStr===hoje()?'var(--purple)':'var(--text-secondary)', marginBottom:8 }}>
                {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][d.getDay()]} {d.getDate()}
              </div>
              {conteudosDoDia(dataStr).map(item => renderCard(item))}
              <button onClick={() => abrirNovo(dataStr)} style={{ width:'100%', marginTop:6, background:'transparent', border:'1px dashed var(--border)', borderRadius:6, padding:'4px', fontSize:11, color:'var(--gray-400)', cursor:'pointer' }}>+ conteúdo</button>
            </div>
          );
        })}
      </div>
    );
  }

  // ---- visão dia ----
  function renderDia() {
    const dataStr = dataRef.toISOString().split('T')[0];
    const itens = conteudosDoDia(dataStr);
    return (
      <div>
        <button onClick={() => abrirNovo(dataStr)} style={{ marginBottom:14, background:'var(--purple)', color:'white', border:'none', padding:'8px 18px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:13 }}>+ Novo conteúdo neste dia</button>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {itens.map(item => {
            const plat = PLATAFORMAS[item.plataforma] || PLATAFORMAS.Outro;
            return (
              <div key={item.id} onClick={() => setItemAberto(item)} className="card" style={{ padding:16, cursor:'pointer', borderLeft:`4px solid ${plat.cor}` }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <div style={{ fontWeight:700, fontSize:14 }}>{plat.icone} {item.plataforma} · {item.formato}</div>
                  {item.concluido ? <span style={{ color:'#16a34a', fontWeight:700 }}>✓ Concluído</span> : <span style={{ color:'#dc2626', fontSize:12 }}>Pendente</span>}
                </div>
                <div style={{ fontSize:13, color:'var(--text-secondary)', marginTop:6 }}>{item.titulo}</div>
                <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:6 }}>{item.status} · {item.responsavel || 'Sem responsável'} · {nomeProjeto(item.projeto_id)}</div>
              </div>
            );
          })}
          {itens.length===0 && <div style={{ textAlign:'center', padding:30, color:'var(--gray-400)' }}>Nenhum conteúdo planejado para este dia.</div>}
        </div>
      </div>
    );
  }

  if (carregando) return <div style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>Carregando calendário...</div>;

  const tituloPeriodo = visao==='mes'
    ? `${nomesMes[dataRef.getMonth()]} ${dataRef.getFullYear()}`
    : visao==='semana'
    ? `Semana de ${dataRef.toLocaleDateString('pt-BR')}`
    : dataRef.toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'long' });

  return (
    <div>
      <div className="section-header"><h2 className="section-title">Calendário de Conteúdo</h2><div className="section-line" /></div>

      <div className="filter-bar">
        <select value={filtroProjeto} onChange={e => setFiltroProjeto(e.target.value)} className={`chip-select${filtroProjeto?' active':''}`}>
          <option value=''>Projeto</option>
          {projetos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
        <div className="tabs-wrap">
          {['mes','semana','dia'].map(v => (
            <button key={v} onClick={() => setVisao(v)} className={`tab-btn${visao===v?' active':''}`} style={{ textTransform:'capitalize' }}>{v}</button>
          ))}
        </div>
        <div style={{ flex:1 }} />
        {acervo.length > 0 && (
          <button onClick={() => setModalAcervo(true)} style={{ background:'#fffbeb', border:'1.5px solid #fde68a', padding:'8px 16px', borderRadius:10, cursor:'pointer', fontSize:12, color:'#92400e', fontFamily:'Poppins,sans-serif', fontWeight:600 }}>
            📦 Acervo ({acervo.length})
          </button>
        )}
        <button onClick={() => setModalExport(true)} style={{ background:'white', border:'1.5px solid rgba(107,33,168,0.15)', padding:'8px 16px', borderRadius:10, cursor:'pointer', fontSize:12, color:'#7B5EA7', fontFamily:'Poppins,sans-serif', fontWeight:500 }}>Exportar PDF</button>
        <button onClick={() => abrirNovo()} style={{ background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', border:'none', padding:'9px 22px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'Poppins,sans-serif', boxShadow:'0 4px 14px rgba(63,0,81,0.3)' }}>+ Novo conteúdo</button>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
        <button onClick={() => navegar(-1)} style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:6, padding:'6px 12px', cursor:'pointer' }}>←</button>
        <span style={{ fontWeight:700, fontSize:15, color:'var(--text-primary)', textTransform:'capitalize' }}>{tituloPeriodo}</span>
        <button onClick={() => navegar(1)} style={{ background:'var(--bg-secondary)', border:'1px solid var(--border)', borderRadius:6, padding:'6px 12px', cursor:'pointer' }}>→</button>
        <button onClick={() => setDataRef(new Date())} style={{ background:'transparent', border:'none', color:'var(--purple)', cursor:'pointer', fontSize:12, fontWeight:600 }}>Hoje</button>
      </div>

      {visao==='mes' && renderMes()}
      {visao==='semana' && renderSemana()}
      {visao==='dia' && renderDia()}

      {/* Painel lateral do item */}
      {itemAberto && (
        <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex' }}>
          <div style={{ flex:1, background:'rgba(0,0,0,0.4)' }} onClick={() => setItemAberto(null)} />
          <div style={{ width:520, background:'#ffffff', borderLeft:'1px solid var(--border)', overflowY:'auto', padding:28, boxShadow:'-4px 0 24px rgba(0,0,0,0.15)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <h3 style={{ margin:0, color:'var(--text-primary)', fontSize:17 }}>{itemAberto.titulo}</h3>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => abrirEditar(itemAberto)} style={{ background:'var(--purple)', color:'white', border:'none', padding:'6px 12px', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600 }}>Editar</button>
                <button onClick={() => excluir(itemAberto)} style={{ background:'#fee2e2', color:'#dc2626', border:'none', padding:'6px 12px', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600 }}>Excluir</button>
                <button onClick={() => setItemAberto(null)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
              </div>
            </div>

            <div className="card" style={{ padding:16, marginBottom:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600 }}>Data</div><span style={{ fontSize:13 }}>{new Date(itemAberto.data+'T00:00').toLocaleDateString('pt-BR')}</span></div>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600 }}>Plataforma</div><span style={{ fontSize:13 }}>{PLATAFORMAS[itemAberto.plataforma]?.icone} {itemAberto.plataforma}</span></div>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600 }}>Formato</div><span style={{ fontSize:13 }}>{itemAberto.formato}</span></div>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600 }}>Responsável</div><span style={{ fontSize:13 }}>{itemAberto.responsavel || '—'}</span></div>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600 }}>Projeto</div><span style={{ fontSize:13 }}>{nomeProjeto(itemAberto.projeto_id)}</span></div>
                <div><div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600 }}>Status</div><span style={{ fontSize:13 }}>{itemAberto.status}</span></div>
                {itemAberto.pilar && <div><div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600 }}>Pilar</div><span style={{ fontSize:13 }}>{itemAberto.pilar}</span></div>}
                {itemAberto.objetivo && <div><div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600 }}>Objetivo</div><span style={{ fontSize:13 }}>{itemAberto.objetivo}</span></div>}
              </div>
            </div>

            {(itemAberto.gancho || itemAberto.legenda) && (
              <div className="card" style={{ padding:14, marginBottom:14 }}>
                {itemAberto.gancho && (
                  <div style={{ marginBottom: itemAberto.legenda ? 10 : 0 }}>
                    <div style={{ fontSize:10, fontWeight:600, color:'var(--purple-rich)', marginBottom:4, textTransform:'uppercase' }}>🎣 Gancho</div>
                    <div style={{ fontSize:13, color:'var(--text-primary)', whiteSpace:'pre-wrap', lineHeight:1.5 }}>{itemAberto.gancho}</div>
                  </div>
                )}
                {itemAberto.legenda && (
                  <div>
                    <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:4, textTransform:'uppercase' }}>📝 Legenda</div>
                    <div style={{ fontSize:13, color:'var(--text-secondary)', whiteSpace:'pre-wrap', lineHeight:1.5, background:'var(--bg-secondary)', padding:'8px 10px', borderRadius:6 }}>{itemAberto.legenda}</div>
                  </div>
                )}
              </div>
            )}

            <div className="card" style={{ padding:14, marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:8, textTransform:'uppercase' }}>Status de produção</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {STATUS_PRODUCAO.map(s => (
                  <button key={s} onClick={() => atualizarCampo(itemAberto, 'status', s)}
                    style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${itemAberto.status===s?'var(--purple)':'var(--border)'}`, background:itemAberto.status===s?'var(--purple)':'transparent', color:itemAberto.status===s?'white':'var(--text-secondary)', cursor:'pointer', fontSize:11, fontWeight:itemAberto.status===s?700:400 }}>{s}</button>
                ))}
              </div>
            </div>

            <div className="card" style={{ padding:14, marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:8, textTransform:'uppercase' }}>Roteiro de gravação</div>
              <textarea defaultValue={itemAberto.roteiro||''} onBlur={e => atualizarCampo(itemAberto, 'roteiro', e.target.value)} rows={8}
                placeholder="Roteiro completo de gravação..."
                style={{ width:'100%', padding:10, border:'1px solid var(--border)', borderRadius:6, fontSize:13, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit' }} />
            </div>

            <div className="card" style={{ padding:14, marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:8, textTransform:'uppercase' }}>Roteiro em .docx (aparece bonito no portal do cliente)</div>
              {itemAberto.roteiro_arquivo ? (
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:12, color:'#16a34a', fontWeight:600 }}>✓ arquivo enviado</span>
                  <button onClick={() => excluirRoteiroDocx(itemAberto)} style={{ background:'transparent', border:'1px solid var(--border)', color:'#dc2626', borderRadius:6, padding:'4px 10px', fontSize:11, cursor:'pointer' }}>Remover</button>
                </div>
              ) : (
                <label style={{ display:'inline-block', background:'var(--bg-secondary)', border:'1px dashed var(--border)', borderRadius:6, padding:'8px 14px', fontSize:12, color:'var(--purple-rich)', cursor:'pointer' }}>
                  {enviandoRoteiro ? 'Enviando...' : '⬆ Escolher arquivo .docx'}
                  <input type="file" accept=".docx" style={{ display:'none' }} disabled={enviandoRoteiro}
                    onChange={e => { const f = e.target.files?.[0]; if (f) enviarRoteiroDocx(itemAberto, f); e.target.value=''; }} />
                </label>
              )}
            </div>

            <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background: itemAberto.requer_gravacao_cliente?'#fef3c7':'var(--bg-secondary)', borderRadius:8, cursor:'pointer', marginBottom:14 }}>
              <input type="checkbox" checked={!!itemAberto.requer_gravacao_cliente} onChange={e => atualizarCampo(itemAberto, 'requer_gravacao_cliente', e.target.checked)} />
              <span style={{ fontSize:13, fontWeight:600, color: itemAberto.requer_gravacao_cliente?'#92400e':'var(--text-secondary)' }}>
                {itemAberto.requer_gravacao_cliente ? '🎥 Cliente precisa gravar este conteúdo' : 'A Vuli produz este conteúdo (cliente não grava)'}
              </span>
            </label>

            <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background: itemAberto.concluido?'#dcfce7':'#fef3c7', borderRadius:8, cursor:'pointer', marginBottom:14 }}>
              <input type="checkbox" checked={!!itemAberto.concluido} onChange={e => atualizarCampo(itemAberto, 'concluido', e.target.checked)} />
              <span style={{ fontSize:13, fontWeight:700, color: itemAberto.concluido?'#16a34a':'#92400e' }}>
                {itemAberto.concluido ? '✓ Publicado e concluído' : 'Marcar como concluído (publicado)'}
              </span>
            </label>

            {itemAberto.status === 'Publicado' && (
              <div className="card" style={{ padding:14 }}>
                <div style={{ fontSize:10, fontWeight:600, color:'var(--gray-400)', marginBottom:8, textTransform:'uppercase' }}>Performance</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                  {PERFORMANCE_OPCOES.map(p => (
                    <button key={p} onClick={() => atualizarCampo(itemAberto, 'performance', p)}
                      style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${itemAberto.performance===p?'#16a34a':'var(--border)'}`, background:itemAberto.performance===p?'#16a34a':'transparent', color:itemAberto.performance===p?'white':'var(--text-secondary)', cursor:'pointer', fontSize:11, fontWeight:itemAberto.performance===p?700:400 }}>{p}</button>
                  ))}
                </div>
                <textarea defaultValue={itemAberto.obs_performance||''} onBlur={e => atualizarCampo(itemAberto, 'obs_performance', e.target.value)} rows={3}
                  placeholder="Observações livres sobre a performance..."
                  style={{ width:'100%', padding:10, border:'1px solid var(--border)', borderRadius:6, fontSize:13, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit' }} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal novo/editar */}
      {modalItem && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}>
          <div className="card" style={{ width:480, padding:32, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color:'var(--purple)' }}>{itemEditando?'Editar conteúdo':'Novo conteúdo'}</h3>
              <button onClick={() => setModalItem(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Projeto</label>
              <select value={form.projeto_id||''} onChange={e => setForm({...form, projeto_id:e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)' }}>
                <option value=''>Selecione...</option>
                {projetos.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Data</label>
                <input type="date" value={form.data} onChange={e => setForm({...form, data:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Plataforma</label>
                <select value={form.plataforma} onChange={e => setForm({...form, plataforma:e.target.value})}
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)' }}>
                  {Object.keys(PLATAFORMAS).map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Formato</label>
              <select value={form.formato} onChange={e => setForm({...form, formato:e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)' }}>
                {FORMATOS.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Tema/título</label>
              <input value={form.titulo} onChange={e => setForm({...form, titulo:e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', boxSizing:'border-box' }} />
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Responsável</label>
              <select value={form.responsavel||''} onChange={e => setForm({...form, responsavel:e.target.value})}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)' }}>
                <option value=''>Selecione...</option>
                {colaboradores.map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Roteiro (opcional agora, dá pra completar depois)</label>
              <textarea value={form.roteiro||''} onChange={e => setForm({...form, roteiro:e.target.value})} rows={4}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', boxSizing:'border-box', resize:'vertical' }} />
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={salvar} style={{ flex:1, background:'var(--purple)', color:'white', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>Salvar</button>
              <button onClick={() => setModalItem(false)} style={{ flex:1, background:'transparent', border:'1px solid var(--border)', padding:'11px', borderRadius:7, cursor:'pointer', color:'var(--text-secondary)', fontSize:14 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal exportar PDF */}
      {modalExport && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}>
          <div className="card" style={{ width:380, padding:32 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, color:'var(--purple)' }}>Exportar para cliente</h3>
              <button onClick={() => setModalExport(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>De</label>
              <input type="date" value={exportInicio} onChange={e => setExportInicio(e.target.value)}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', boxSizing:'border-box' }} />
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:'var(--gray-400)', marginBottom:5, textTransform:'uppercase' }}>Até</label>
              <input type="date" value={exportFim} onChange={e => setExportFim(e.target.value)}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', boxSizing:'border-box' }} />
            </div>
            <div style={{ fontSize:11, color:'var(--gray-400)', marginBottom:16 }}>O PDF mostra só data, plataforma, formato e tema — sem roteiro ou observações internas.</div>
            <button onClick={exportarPDF} style={{ width:'100%', background:'var(--purple)', color:'white', border:'none', padding:'11px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:14 }}>Gerar PDF</button>
          </div>
        </div>
      )}

      {/* Modal Acervo — conteúdos ainda sem data */}
      {modalAcervo && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100 }}>
          <div className="card" style={{ width:560, padding:32, maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
              <h3 style={{ margin:0, color:'var(--purple)' }}>📦 Acervo</h3>
              <button onClick={() => setModalAcervo(false)} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:'var(--gray-400)' }}>×</button>
            </div>
            <p style={{ fontSize:12, color:'var(--gray-400)', marginBottom:16 }}>
              Conteúdos sem data ainda. Vídeos de TikTok entram sozinhos no calendário conforme os dias vão passando —
              os outros (criativos/Instagram) esperam o cliente escolher no portal. Se quiser, agende manualmente aqui.
            </p>
            {acervo.length === 0 ? (
              <div style={{ textAlign:'center', padding:24, color:'var(--gray-400)', fontSize:13 }}>Acervo vazio.</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {acervo.map(item => (
                  <div key={item.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'var(--bg-secondary)', borderRadius:8, gap:10, flexWrap:'wrap' }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)' }}>{item.titulo}</div>
                      <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:2 }}>
                        {item.formato} · {item.plataforma} · {nomeProjeto(item.projeto_id)}
                        {item.escolhido_em && <span style={{ color:'#16a34a', fontWeight:600 }}> · ✓ cliente escolheu</span>}
                      </div>
                    </div>
                    <input type="date" onChange={e => e.target.value && agendarDoAcervo(item, e.target.value)}
                      style={{ padding:'6px 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:12, background:'white' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
